import { AutoplayMode } from '@dicetechnology/dice-doris';
import type { PlaybackUrlData, ResolveStreamInfoResponse, StreamData } from '@dicetechnology/dice-doris';
import { EContentType } from '@dicetechnology/dice-unity/lib/types/content';
import type { MovieMediaMetadataExtended } from '@dicetechnology/doris-plugin-chromecast-receiver';
import type { cast as castNs } from 'chromecast-caf-receiver';
import type { MediaInformation } from 'chromecast-caf-receiver/cast.framework.messages';

import type { DiceCustomData } from '../types';

import { mapExtendedSkipMarker } from './mapExtendedSkipMarker';
import { mapSkipMarker } from './mapSkipMarker';

declare const cast: typeof castNs;

const getMetadataCompat = (
    streamInfoOrStreamData: ResolveStreamInfoResponse<PlaybackUrlData> | ResolveStreamInfoResponse<PlaybackUrlData[]> | StreamData
) => {
    if ('type' in streamInfoOrStreamData) {
        // StreamData (v5)
        const streamData = streamInfoOrStreamData;
        const vodMetadata =
            streamData.type === EContentType.VOD
                ? {
                      annotations: streamData.annotations,
                      autoplayMode: streamData.autoplayMode,
                      extendedSkipMarkers: streamData.extendedSkipMarkers,
                      skipMarkers: streamData.skipMarkers,
                      thumbnailsPreview: streamData.thumbnailsPreview
                  }
                : undefined;
        return {
            ...vodMetadata,
            title: streamData.title,
            description: streamData.description,
            thumbnailUrl: streamData.thumbnailUrl
        };
    } else {
        // StreamInfo (v4)
        const streamInfo = streamInfoOrStreamData;
        return {
            annotations: streamInfo.annotations,
            autoplayMode: streamInfo.autoplayMode,
            description: streamInfo.metadata?.description,
            extendedSkipMarkers: streamInfo.extendedSkipMarkers,
            skipMarkers: streamInfo.skipMarkers,
            title: streamInfo.metadata?.title,
            thumbnailUrl: streamInfo.metadata?.thumbnailUrl,
            thumbnailsPreview: streamInfo.metadata?.thumbnailsPreview
        };
    }
};

interface EnrichMediaMetadataOptions {
    media: MediaInformation;
    streamInfoOrStreamData: ResolveStreamInfoResponse<PlaybackUrlData> | ResolveStreamInfoResponse<PlaybackUrlData[]> | StreamData;
}

export const enrichMediaMetadata = (options: EnrichMediaMetadataOptions): void => {
    const { media, streamInfoOrStreamData } = options;

    const mediaCustomData: DiceCustomData = (media.customData ??= {});
    const extendedMetadata = (mediaCustomData.extendedMetadata ??= {});
    const mediaMetadata: MovieMediaMetadataExtended = (media.metadata ??= new cast.framework.messages.MovieMediaMetadata());

    const { description, thumbnailUrl, title, annotations, skipMarkers, extendedSkipMarkers, thumbnailsPreview, autoplayMode } =
        getMetadataCompat(streamInfoOrStreamData);

    // Media Metadata
    if (title && !mediaMetadata.title) {
        mediaMetadata.title = title;
    }

    if (description) {
        if (!mediaMetadata.subtitle) {
            mediaMetadata.subtitle = description;
        }
        if (!mediaMetadata.description) {
            mediaMetadata.description = description;
        }
    }

    if (thumbnailUrl) {
        if (mediaMetadata.images == null || mediaMetadata.images.length === 0) {
            mediaMetadata.images = [new cast.framework.messages.Image(thumbnailUrl)];
        }
        if (!mediaMetadata.posterUrl) {
            mediaMetadata.posterUrl = thumbnailUrl;
        }
    }

    // Media Custom Data (Extended Metadata)
    if (annotations) {
        if (!extendedMetadata.annotationThumbnailsVttUrl) {
            extendedMetadata.annotationThumbnailsVttUrl = annotations.thumbnails;
        }
        if (!extendedMetadata.annotationTitlesVttUrl) {
            extendedMetadata.annotationTitlesVttUrl = annotations.titles;
        }
    }

    if (skipMarkers && (extendedMetadata.skipMarkers == null || extendedMetadata.skipMarkers.length === 0)) {
        extendedMetadata.skipMarkers = skipMarkers.map(mapSkipMarker).sort((a, b) => a.start - b.start);
    }

    if (extendedSkipMarkers && (extendedMetadata.extendedSkipMarkers == null || extendedMetadata.extendedSkipMarkers.length === 0)) {
        extendedMetadata.extendedSkipMarkers = extendedSkipMarkers.map(mapExtendedSkipMarker).sort((a, b) => a.start - b.start);
    }

    if (autoplayMode && autoplayMode !== AutoplayMode.ALLOWED && !extendedMetadata.autoplayMode) {
        extendedMetadata.autoplayMode = autoplayMode;
    }

    if (thumbnailsPreview && !extendedMetadata.thumbnailsBifUrl) {
        extendedMetadata.thumbnailsBifUrl = thumbnailsPreview;
    }
};
